import { Union, debounce, filter, map } from '../../lib'

import httpClient from '../../api/httpClient'
import { makeStoreUser } from '../../lib/types/StoreUser'
import { update } from '../../lib/vuex'

const fetchStore = ({ commit }, id) =>
  httpClient
    .get(`/stores/${id}`)
    .then((response) => commit('setStore', response.data))
    .catch((error) => console.error(error))

const fetchStoreUsers = async ({ commit }, id) => {
  commit('update', { path: 'usersStatus', value: Status.Processing() })

  try {
    const { data } = await httpClient.get(`/stores/${id}/users`)
    const storeUsers = data.map((user) => makeStoreUser(user))
    commit('setStoreUsers', storeUsers)
    commit('update', { path: 'usersStatus', value: Status.Done() })
    commit('update', { path: 'usersStatus', value: Status.Done() })
  } catch {
    return commit('update', { path: 'usersStatus', value: Status.Failed() })
  }
}

const fetchRegisterLinks = async ({ commit }, id) => {
  commit('update', { path: 'registerLinksStatus', value: Status.Processing() })

  try {
    const response = await httpClient.get(`/stores/${id}/permissions?register=1`)
    commit('setRegisterLinks', response.data)
    commit('update', { path: 'registerLinksStatus', value: Status.Done() })
    return response
  } catch (error) {
    commit('update', { path: 'registerLinksStatus', value: Status.Done() })
    console.error(error)
  }
}

const findUsers = async (commit, state, query) => {
  commit('update', { path: 'loading', value: true })

  const validateEmail = (email) => {
    return email.match(
      // eslint-disable-next-line
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    )
  }

  try {
    const { data } = await httpClient.get(`/users/find`, {
      params: { query },
    })
    query === state.ui.usersList.value &&
      commit('updateUsersList', {
        path: 'suggestions',
        value: map(data.data, (user) => ({
          ...user,
          name: `${user.id}${user.email ? ` - ${user.email}` : ''}`,
        })),
      })

    if (validateEmail(query) && !state.ui.usersList.suggestions.length) {
      commit('update', { path: 'registerLink', value: true })
      state.ui.usersList.suggestions.unshift({ name: `Invite ${query}`, email: query })
    }
  } catch (error) {
    console.error(error)
  } finally {
    commit('update', { path: 'loading', value: false })
  }
}

findUsers.delayed = debounce(findUsers, 250)

export const Status = Union('StatusStoreUsers', {
  Idle: [],
  Processing: [],
  Done: [],
  Failed: [],
})

export default {
  state: {
    ui: {
      store: {
        feed_name: null,
      },
      storeUsers: null,
      usersStatus: Status.Idle(),
      registerLinksStatus: Status.Idle(),
      registerLinks: null,
      loading: false,
      permissions: {
        new: {
          cost: false,
          users: false,
          sales: false,
          media: false,
          edit: true,
          voucher_code: true,
          find_influencers: true,
        },
      },
      registerLink: false,
      usersList: {
        suggestions: [],
        value: '',
        selected: {},
      },
      userEmail: '',
      storePermissionError: '',
      storePermissionSuccess: '',
      permissionsList: [
        {
          name: 'cost',
          label: 'Cost',
        },
        {
          name: 'users',
          label: 'Users',
        },
        {
          name: 'sales',
          label: 'Sales',
        },
        {
          name: 'media',
          label: 'Media',
        },
        {
          name: 'edit',
          label: 'Edit',
          checkedByDefault: 'checked',
        },
        {
          name: 'voucher_code',
          label: 'Voucher Codes',
          checkedByDefault: 'checked',
        },
        {
          name: 'find_influencers',
          label: 'Influencers search',
          checkedByDefault: 'checked',
        },
      ],
    },
  },

  mutations: {
    setStore: (state, store) => {
      state.ui.store = store
      const editPermissionIndex = state.ui.permissionsList.findIndex((i) => i.name === 'edit')
      if (editPermissionIndex !== -1) {
        state.ui.permissionsList[editPermissionIndex] = {
          ...state.ui.permissionsList[editPermissionIndex],
          checkedByDefault: store.store_type === 'MANAGED' ? 'checked' : false,
          disabled: store.store_type === 'MANAGED',
        }
      }
    },
    setStoreUsers: (state, storeUsers) => {
      state.ui.storeUsers = storeUsers
    },
    setRegisterLinks: (state, registerLinks) => {
      state.ui.registerLinks = registerLinks
    },
    update(state, { path, value }) {
      state.ui = update(state.ui, path, value)
    },
    updateUsersList: (state, { path, value }) => {
      state.ui.usersList[path] = value
    },
    updateStoreUsers: (state, storeUsers) => {
      state.ui.storeUsers = storeUsers
    },
  },

  getters: {
    usersStore: ({ ui: { store } }) => store,
    storeUsers: ({ ui: { storeUsers } }) => storeUsers,
    usersStatus: ({ ui: { usersStatus } }) => usersStatus,
    registerLinks: ({ ui: { registerLinks } }) => registerLinks,
    registerLinksStatus: ({ ui: { registerLinksStatus } }) => registerLinksStatus,
    permissions: ({ ui: { permissions } }) => permissions,
    permissionsList: ({ ui: { permissionsList } }) => permissionsList,
    usersList: ({ ui: { usersList } }) => usersList,
    registerLink: ({ ui: { registerLink } }) => registerLink,
    userEmail: ({ ui: { userEmail } }) => userEmail,
    storePermissionError: ({ ui: { storePermissionError } }) => storePermissionError,
    storePermissionSuccess: ({ ui: { storePermissionSuccess } }) => storePermissionSuccess,
    loading: ({ ui: { loading } }) => loading,
  },

  actions: {
    init({ commit, dispatch, rootGetters, getters, state }, { id }) {
      fetchStore({ rootGetters, commit }, id)
      fetchStoreUsers({ commit, dispatch, rootGetters, getters }, id)
      fetchRegisterLinks({ commit, rootGetters }, id)
      findUsers(commit, state, '')
    },

    async createPermissions({ commit, dispatch }, { id, userId, email, permissions }) {
      if (!userId && email) {
        return dispatch('createRegisterLink', { id, email, permissions })
      }

      commit('update', { path: 'loading', value: true })
      commit('update', { path: 'storePermissionError', value: '' })
      commit('update', { path: 'storePermissionSuccess', value: '' })

      try {
        const response = await httpClient.post(`/stores/${id}/permissions`, {
          hide: permissions,
          user_id: userId,
        })
        commit('update', { path: 'storePermissionSuccess', value: 'Permissions were successfully created' })
        await fetchStoreUsers({ commit }, id)
        return response
      } catch (error) {
        commit('update', { path: 'storePermissionError', value: 'User is not selected' })
        return error
      } finally {
        commit('update', { path: 'loading', value: false })
      }
    },

    async createRegisterLink({ commit, getters }, { id, email, permissions }) {
      commit('update', { path: 'loading', value: true })
      commit('update', { path: 'storePermissionError', value: '' })
      commit('update', { path: 'registerLinksStatus', value: Status.Processing() })
      commit('update', { path: 'storePermissionSuccess', value: '' })

      try {
        const response = await httpClient.post(`/stores/${id}/register-link`, {
          hide: permissions,
          email: email,
        })
        commit('update', { path: 'registerLinksStatus', value: Status.Done() })
        commit('update', { path: 'storePermissionSuccess', value: 'Register link was successfully created' })
        getters.registerLinks.push(response.data)
        return response
      } catch (error) {
        return error
      } finally {
        commit('update', { path: 'loading', value: false })
      }
    },

    async deletePermissions({ commit, getters }, { id, userId }) {
      commit('update', { path: 'loading', value: true })
      commit('update', { path: 'storePermissionError', value: '' })
      commit('update', { path: 'storePermissionSuccess', value: '' })
      try {
        const response = await httpClient.delete(`/stores/${id}/permissions/${userId}`)
        const storeUsers = filter(getters.storeUsers, (storeUser) => {
          if (storeUser.id === userId) {
            return false
          }
          return true
        })
        commit('updateStoreUsers', storeUsers)
        commit('update', { path: 'storePermissionSuccess', value: 'User has been successfully deleted!' })
        return response
      } catch (error) {
        commit('update', { path: 'storePermissionError', value: error })
        return error
      } finally {
        commit('update', { path: 'loading', value: false })
      }
    },

    update({ commit }, { path, value }) {
      commit('update', { path, value })
    },

    updateUsersList({ commit }, { path, value }) {
      commit('updateUsersList', { path, value })
    },

    findUsers({ commit, state }, query) {
      findUsers.delayed(commit, state, query)
    },
  },
}
